import { useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/field';
import { rawRequest } from '@/api/client';
import { ApiError } from '@/api/errors';

interface IconFieldProps {
  /**
   * Reports the pending change to the parent, which decides what to send:
   *   never called — untouched, so the save omits `iconKey` entirely
   *   `string`     — a freshly uploaded key to save
   *   `null`       — cleared
   *
   * There is no `value` prop, because the admin API no longer returns the
   * stored key — the URL is `{API}/api/v1/media/public/{key}` and carried the
   * same information twice. What the field shows is the preview.
   */
  onChange: (iconKey: string | null) => void;
  /** The icon already on the row, if any. */
  initialPreviewUrl?: string | null;
  /** Interests require one; categories do not. */
  required?: boolean;
  error?: string;
}

/**
 * Picks a master-data icon by uploading the image.
 *
 * ⚠️ **This replaced a free-text field that asked an admin to type a storage
 * key.** That field assumed the file had already been put into storage by some
 * other means and that whoever filled it in knew the exact path. A typo was not
 * rejected — the storage driver signs a URL for any key it is handed — so the
 * row saved cleanly and every client rendered a broken image instead. The key
 * now only ever comes back from the upload endpoint.
 *
 * The preview is deliberately the signed URL the server returns rather than a
 * local `URL.createObjectURL`: it proves the bytes made the round trip and are
 * readable through the same signing path the app will use, so a storage
 * misconfiguration shows up here rather than after saving.
 */
export function IconField({
  onChange,
  initialPreviewUrl = null,
  required = false,
  error,
}: IconFieldProps) {
  const inputId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function upload(file: File): Promise<void> {
    setUploading(true);
    setUploadError(null);

    try {
      const body = new FormData();
      body.append('file', file);

      // No Content-Type header: the browser has to set it so the multipart
      // boundary matches the body, and naming it by hand omits the boundary.
      const response = await rawRequest('/admin/icons', { method: 'POST', body });
      const result = (await response.json()) as { iconKey: string; iconUrl: string };

      onChange(result.iconKey);
      setPreviewUrl(result.iconUrl);
    } catch (err) {
      setUploadError(
        err instanceof ApiError
          ? (err.message ?? 'The image could not be uploaded')
          : 'The image could not be uploaded',
      );
    } finally {
      setUploading(false);
      // Cleared so choosing the SAME file again still fires onChange — the
      // input does not emit an event when the value is unchanged.
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <FormField
      label="Icon"
      htmlFor={inputId}
      hint="PNG, JPEG or WebP, up to 5 MB. Resized to 256px and re-encoded to WebP."
      error={error ?? uploadError ?? undefined}
      required={required}
    >
      <div className="flex items-start gap-3">
        <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="size-full object-contain" />
          ) : (
            <span className="text-[10px] text-muted-foreground">None</span>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <input
            id={inputId}
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {previewUrl ? 'Replace image' : 'Upload image'}
            </Button>

            {/* Only when clearing is allowed. An interest without an icon
                cannot be saved, so offering the button would be a dead end. */}
            {previewUrl && !required ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={uploading}
                onClick={() => {
                  onChange(null);
                  setPreviewUrl(null);
                }}
              >
                Remove
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </FormField>
  );
}
